/** Cart milestones and the single selectable gift shared by both cart views. */
(() => {
  const configElement = document.getElementById('Cart-Milestone-Config');
  if (!configElement) return;

  const makeTier = (number) => ({
    number,
    // Hyphens before digits remain in dataset keys (for example, tier-2Code).
    amount: Number(configElement.getAttribute(`data-tier-${number}-amount`)) || 0,
    code: (configElement.getAttribute(`data-tier-${number}-code`) || '').trim(),
    get enabled() {
      return this.amount > 0 && Boolean(this.code) && Boolean(document.querySelector(`.cart-milestones__step[data-tier="${number}"]`));
    }
  });
  const tiers = [2, 3].map(makeTier);
  const same = (a, b) => Boolean(a && b) && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const endpoint = (path) => `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}${path}`;
  const sectionTargets = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ];
  const sectionNames = () => sectionTargets.filter(({ id }) => document.getElementById(id)).map(({ section }) => section);
  const sectionsPayload = () => ({ sections: sectionNames(), sections_url: window.location.pathname });
  let renderingSections = false;

  async function request(path, body) {
    const response = await fetch(endpoint(path), body === undefined ? {
      headers: { Accept: 'application/json' }
    } : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || 'The cart could not be updated.');
    }
    return result;
  }

  function giftLines(cart) {
    return (cart.items || []).filter((item) => item.properties?._cart_milestone_gift);
  }

  function codesInCart(cart) {
    const codes = (cart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
    (cart.cart_level_discount_applications || []).forEach((discount) => {
      if (discount.type === 'discount_code' && discount.title) codes.push(discount.title);
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const discount = allocation.discount_application;
        if (discount?.type === 'discount_code' && discount.title) codes.push(discount.title);
      });
    });
    return codes.filter((code, index) => codes.findIndex((other) => same(other, code)) === index);
  }

  function discountApplied(cart, code) {
    const discount = (cart.discount_codes || []).find((entry) => same(entry.code, code));
    if (discount?.applicable === false) return false;
    if (discount?.applicable === true) return true;
    return (cart.cart_level_discount_applications || []).some((entry) => entry.type === 'discount_code' && same(entry.title, code) && entry.total_allocated_amount > 0)
      || (cart.items || []).some((item) => (item.line_level_discount_allocations || []).some((allocation) =>
        allocation.amount > 0 && allocation.discount_application?.type === 'discount_code' && same(allocation.discount_application.title, code)));
  }

  function verifyGiftDiscount(cart, code) {
    if (!discountApplied(cart, code)) {
      throw new Error('The free gift discount could not be applied. Please try again.');
    }
  }

  function showGiftError(tierNumber, message) {
    document.querySelectorAll(`cart-milestone-rewards[data-tier="${tierNumber}"]`).forEach((rewards) => {
      const error = rewards.querySelector('.cart-rewards__error');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      rewards.discountFailed = Boolean(message);
      rewards.cards?.forEach((card) => rewards.updateVariant(card));
    });
  }

  function isMilestoneCode(code) {
    return tiers.some((tier) => same(code, tier.code));
  }

  function qualifyingTotal(cart) {
    let total = Number(cart.total_price) || 0;
    giftLines(cart).forEach((item) => {
      total -= Number(item.final_line_price) || 0;
    });
    return Math.max(0, total);
  }

  function activeTier(total) {
    return tiers.findLast((tier) => tier.enabled && total >= tier.amount)?.number || 0;
  }

  function renderSections(state) {
    if (Array.isArray(state?.items)) scheduleReconcile(state);
    if (!state?.sections) return true;
    let missingSection = false;
    sectionTargets.forEach(({ id, section, selector }) => {
      const root = document.getElementById(id);
      const html = state.sections[section];
      if (!root) return;
      if (!html) {
        missingSection = true;
        return;
      }
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      const target = root.querySelector(selector) || root;
      if (source) {
        const scrollTop = id === 'Cart-Drawer' ? root.querySelector('.side-panel-inner')?.scrollTop : null;
        renderingSections = true;
        try {
          target.innerHTML = source.innerHTML;
        } finally {
          renderingSections = false;
        }
        const scrollContainer = root.querySelector('.side-panel-inner');
        if (scrollTop != null && scrollContainer) scrollContainer.scrollTop = scrollTop;
        if (id === 'Cart-Drawer') root.removeProductEvent?.();
        if (id === 'Cart') {
          window.cartPage?.removeProductEvent?.();
          window.cartPage?.termsCheckbox?.();
        }
      } else missingSection = true;
    });
    return missingSection;
  }

  async function refreshSections() {
    const response = await fetch(`${window.location.pathname}?sections=${sectionNames().join(',')}`);
    if (!response.ok) return;
    renderSections({ sections: await response.json() });
  }

  let busy = false;
  let reconcileTimer;
  let lastGiftDiscountAttempt;
  async function reconcile(currentCart) {
    if (busy) {
      scheduleReconcile(currentCart);
      return;
    }
    busy = true;
    try {
      const cart = currentCart || await request('cart.js');
      const amount = qualifyingTotal(cart);
      const gifts = giftLines(cart);
      const codes = codesInCart(cart);
      const keepTier = tiers.findLast((tier) => tier.enabled && amount >= tier.amount
        && gifts.some((gift) => Number(gift.properties._cart_milestone_gift) === tier.number));
      const keep = gifts.find((gift) => Number(gift.properties._cart_milestone_gift) === keepTier?.number);
      const updates = {};
      gifts.forEach((gift) => {
        if (gift !== keep) updates[gift.key] = 0;
        else if (gift.quantity !== 1) updates[gift.key] = 1;
      });
      const keepCode = keepTier?.code;
      if (!keepCode || discountApplied(cart, keepCode)) lastGiftDiscountAttempt = null;
      const desiredCodes = codes.filter((code) => !isMilestoneCode(code));
      if (keepCode) desiredCodes.push(keepCode);
      const staleCode = codes.some((code) => isMilestoneCode(code) && !same(code, keepCode));
      const repairKey = keepCode && JSON.stringify([keep.properties._cart_milestone_token || keep.variant_id, keepCode, amount]);
      const repairDiscount = keepCode && !discountApplied(cart, keepCode) && repairKey !== lastGiftDiscountAttempt;
      if (Object.keys(updates).length || staleCode || repairDiscount) {
        // Section rendering schedules reconciliation again; retry only once per gift/amount.
        if (keepCode) lastGiftDiscountAttempt = repairKey;
        const state = await request('cart/update.js', {
          updates,
          discount: desiredCodes.join(','),
          ...sectionsPayload()
        });
        if (renderSections(state)) await refreshSections();
        if (keepCode) {
          try {
            verifyGiftDiscount(state, keepCode);
            showGiftError(keep.properties._cart_milestone_gift, '');
          } catch (error) {
            showGiftError(keep.properties._cart_milestone_gift, error.message);
          }
        }
      }
    } catch (error) {
      console.error('Cart milestone reconciliation failed:', error);
    } finally {
      busy = false;
    }
  }

  function scheduleReconcile(eventOrCart) {
    // Cart changes already return the complete state; reuse it instead of fetching again.
    const state = eventOrCart?.detail?.cart || eventOrCart;
    const cart = Array.isArray(state?.items) && typeof state.total_price === 'number' ? state : null;
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(() => reconcile(cart), 120);
  }

  async function restoreCart(oldGifts, previousCodes, token) {
    const cart = await request('cart.js');
    const addedGift = giftLines(cart).find((item) => item.properties?._cart_milestone_token === token);
    const updates = addedGift ? { [addedGift.key]: 0 } : {};
    const missingGifts = oldGifts.filter((gift) => !giftLines(cart).some((item) => item.properties?._cart_milestone_token === gift.properties?._cart_milestone_token));
    if (!missingGifts.length) {
      return request('cart/update.js', { updates, discount: previousCodes.join(','), ...sectionsPayload() });
    }
    await request('cart/update.js', { updates, discount: previousCodes.join(',') });
    await request('cart/add.js', {
      items: missingGifts.map((gift) => ({ id: gift.variant_id, quantity: gift.quantity, properties: gift.properties }))
    });
    return request('cart/update.js', { discount: previousCodes.join(','), ...sectionsPayload() });
  }

  async function chooseGift(tierNumber, variantId) {
    if (busy) throw new Error('Please wait for the cart to update.');
    busy = true;
    const tier = tiers.find((entry) => entry.number === tierNumber);
    let oldGifts = [];
    let previousCodes = [];
    let token;
    try {
      const before = await request('cart.js');
      oldGifts = giftLines(before);
      previousCodes = codesInCart(before);
      if (!tier?.code || tier.amount <= 0) {
        throw new Error('This gift is currently unavailable. Please refresh the page and try again.');
      }
      if (!tier?.enabled || activeTier(qualifyingTotal(before)) !== tierNumber) {
        throw new Error('Your cart no longer qualifies for this gift.');
      }
      if (oldGifts.length === 1 && oldGifts[0].variant_id === variantId && Number(oldGifts[0].properties._cart_milestone_gift) === tierNumber) {
        const desiredCodes = previousCodes.filter((code) => !isMilestoneCode(code));
        desiredCodes.push(tier.code);
        const state = await request('cart/update.js', { discount: desiredCodes.join(','), ...sectionsPayload() });
        scheduleReconcile(state);
        verifyGiftDiscount(state, tier.code);
        if (qualifyingTotal(state) < tier.amount) throw new Error('Your cart no longer qualifies for this gift.');
        if (renderSections(state)) await refreshSections();
        return;
      }
      token = `milestone-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      await request('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: {
          _cart_milestone_gift: String(tierNumber),
          _cart_milestone_token: token
        } }]
      });
      const updates = {};
      if (oldGifts.length) {
        // Adding a gift can change discounted line keys. Read fresh keys only when replacing one.
        const withNewGift = await request('cart.js');
        giftLines(withNewGift).forEach((item) => {
          if (item.properties._cart_milestone_token !== token) updates[item.key] = 0;
        });
      }
      const desiredCodes = previousCodes.filter((code) => !isMilestoneCode(code));
      desiredCodes.push(tier.code);
      const after = await request('cart/update.js', {
        updates,
        discount: desiredCodes.join(','),
        ...sectionsPayload()
      });
      const newGift = giftLines(after).find((item) => item.properties?._cart_milestone_token === token);
      if (!newGift) {
        throw new Error('The gift could not be added to your cart. Please try again.');
      }
      verifyGiftDiscount(after, tier.code);
      if (qualifyingTotal(after) < tier.amount) throw new Error('Your cart no longer qualifies for this gift.');
      if (renderSections(after)) await refreshSections();
    } catch (error) {
      if (token) {
        try {
          if (renderSections(await restoreCart(oldGifts, previousCodes, token))) await refreshSections();
        } catch (rollbackError) {
          console.error('Cart milestone rollback failed:', rollbackError);
          await refreshSections();
        }
      } else {
        await refreshSections();
      }
      throw error;
    } finally {
      busy = false;
    }
  }

  class CartMilestoneProgress extends HTMLElement {
    connectedCallback() {
      if (!renderingSections) scheduleReconcile();
      const steps = [...this.querySelectorAll('[data-threshold]')];
      if (!steps.length) return;
      const total = Number(this.dataset.total) || 0;
      const positions = steps.map((_, index) => (index + .5) / steps.length * 100);
      let progress = 0;
      if (total >= Number(steps[steps.length - 1].dataset.threshold)) {
        progress = 100;
      } else {
        for (let index = 0; index < steps.length; index++) {
          const threshold = Number(steps[index].dataset.threshold);
          if (total < threshold) {
            const previousThreshold = index ? Number(steps[index - 1].dataset.threshold) : 0;
            const previousPosition = index ? positions[index - 1] : 0;
            progress = previousPosition + (total - previousThreshold) / Math.max(1, threshold - previousThreshold) * (positions[index] - previousPosition);
            break;
          }
        }
      }
      this.style.setProperty('--milestone-progress', `${Math.max(0, Math.min(100, progress))}%`);
    }
  }

  class CartMilestoneRewards extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.cardsContainer = this.querySelector('.cart-rewards__cards');
      this.index = 0;
      this.querySelectorAll('[data-direction]').forEach((button) => {
        button.addEventListener('click', () => this.showCard(this.index + Number(button.dataset.direction)), { signal });
      });
      if (this.cards.length > 1) {
        this.cardsContainer.addEventListener('scroll', () => {
          const step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
          if (step > 0) this.index = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.cardsContainer.scrollLeft / step)));
        }, { passive: true, signal });
      }
      this.cards.forEach((card) => {
        card.variants = JSON.parse(card.querySelector('[data-variants]').textContent);
        card.querySelectorAll('[data-option-index]').forEach((select) => {
          // Keep every value so switching an earlier option can restore available choices.
          select.optionChoices ||= [...select.options].map((option) => option.cloneNode(true));
          select.addEventListener('change', () => this.updateVariant(card), { signal });
        });
        card.querySelector('[data-add-gift]').addEventListener('click', () => this.addGift(card), { signal });
        this.updateVariant(card);
      });
    }

    disconnectedCallback() {
      this.events?.abort();
    }

    showCard(index) {
      if (this.cards.length < 2) return;
      this.index = (index + this.cards.length) % this.cards.length;
      this.cardsContainer.scrollTo({
        left: this.cards[this.index].offsetLeft - this.cards[0].offsetLeft,
        behavior: 'smooth'
      });
    }

    updateVariant(card) {
      let availableVariants = card.variants.filter((variant) => variant.available);
      const selections = [...card.querySelectorAll('[data-option-index]')].map((select) => {
        const optionIndex = Number(select.dataset.optionIndex);
        const previousValue = select.value;
        const availableValues = new Set(availableVariants.map((variant) => variant.options[optionIndex]));
        const choices = select.optionChoices.filter((option) => availableValues.has(option.value));
        if (select.options.length !== choices.length || choices.some((option, index) => select.options[index].value !== option.value)) {
          select.replaceChildren(...choices.map((option) => option.cloneNode(true)));
        }
        select.value = availableValues.has(previousValue) ? previousValue : choices[0]?.value || '';
        select.disabled = !choices.length;
        select.closest('label').hidden = !choices.length;
        // Later options depend on the choices above them (for example, sizes for a color).
        availableVariants = availableVariants.filter((variant) => variant.options[optionIndex] === select.value);
        return select.value;
      });
      const variant = card.variants.find((candidate) => selections.every((value, index) => candidate.options[index] === value));
      card.variant = variant;
      const button = card.querySelector('[data-add-gift]');
      const selected = !this.discountFailed && variant && Number(this.dataset.selectedVariant) === Number(variant.id);
      button.disabled = Boolean(this.adding) || !variant?.available || selected;
      button.textContent = this.adding === card ? 'ADDING...' : selected ? 'SELECTED' : variant?.available ? 'ADD TO CART' : 'SOLD OUT';
      if (variant && typeof formatMoney === 'function') {
        const currentPrice = card.querySelector('[data-current-price]');
        currentPrice.innerHTML = formatMoney(variant.price, window.theme?.settings?.money_with_currency_format);
      }
      const image = card.querySelector('.cart-rewards__image img');
      if (image) {
        const colorIndex = Number(card.dataset.colorOptionIndex);
        const sameColorVariant = colorIndex >= 0
          ? card.variants.find((candidate) => candidate.options[colorIndex] === selections[colorIndex] && candidate.image)
          : null;
        const imageUrl = variant?.image || sameColorVariant?.image || card.dataset.defaultImage;
        if (imageUrl && image.getAttribute('src') !== imageUrl) {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        }
      }
    }

    async addGift(card) {
      const variant = card.variant;
      if (!variant?.available || this.adding) return;
      const error = this.querySelector('.cart-rewards__error');
      error.hidden = true;
      this.adding = card;
      this.cards.forEach((item) => this.updateVariant(item));
      try {
        await chooseGift(Number(this.dataset.tier), Number(variant.id));
      } catch (failure) {
        const message = failure.message || 'The gift could not be added.';
        showGiftError(this.dataset.tier, message);
      } finally {
        this.adding = null;
        if (this.isConnected) this.cards.forEach((item) => this.updateVariant(item));
      }
    }
  }

  customElements.define('cart-milestone-progress', CartMilestoneProgress);
  customElements.define('cart-milestone-rewards', CartMilestoneRewards);
  document.addEventListener('line-item:change:end', scheduleReconcile);
  document.addEventListener('cart:item-added', scheduleReconcile);
  document.addEventListener('cart:refresh', scheduleReconcile);
  scheduleReconcile();
})();
